import _ from "lodash"
import { differenceInYears, setDate, subYears, getYear, getMonth, getDate } from "date-fns"
import { List } from "immutable"
import fp from "lodash/fp"
import { createSelector } from "reselect"
import { getValues } from "redux-form"

import {
  reservationFormSelector,
  containsFlightSelector,
  isInsuranceEnabledInReservationSelector,
} from "booking/services/reservation/selectors/reservationSelectors"
import {
  getDocumentNeed,
  getOriginalPassengers,
  getThresholds,
} from "booking/services/states/reducer"
import languages from "../../../../../data/languages.json" // eslint-disable-line import/no-restricted-paths
import Passenger, {
  PASSENGER_CATEGORIES,
} from "../../../../../app/scripts/containers/mutable/Passenger"
import PassengersCount from "common/records/PassengersCount"
import shouldRequireBirthCertificate from "common/services/utils/shouldRequireBirthCertificate"
import { getJourney } from "../../journey/reducer"
import { getInfantsConditions } from "../../infantsConditions/reducer"
import {
  formatFirstNamePlaceholder,
  formatLastnNamePlaceholder,
  formatMiddleNamePlaceholder,
} from "../../../scenes/ReservationForm/services/helpers/passengerNamesFormatter"
import { shouldShowMiddlename } from "common/services/utils/middlenameValidationSettings"
import shouldRequirePassportExpirationDate from "common/services/utils/shouldRequirePassportExpirationDate"
import { getLanguageInfo } from "common/services/staticData"
import translate from "../../../../../app/scripts/tools/translate"
import { INTERNATIONAL_PASSPORT_NATIONALITIES } from "common/consts/passengers"
import { isInsuranceAllowedForNationality } from "../../../../../app/scripts/tools/insurance"
import { getAffilId } from "common/services/options/selectors"
import { isSetAffilId } from "common/services/affilId/tools"
import { getCombinations } from "../../baggage/reducer"
import { getBaggageInitialCombinations } from "../../baggage/selectors/baggageInitialCombinationsSelector"
import { storedPassengersSelector } from "../../storedPassengers/reducer"
import paxObjectMapper from "../helpers/paxObjectMapper"

const AFFILID_BAGS_RECHECK_ALWAYS = "skiplagged"

// Exported selectors
// ==
// Grabs passengers directly from form state.
// WARNING: Do not use until reservation form is fully loaded.
// TODO: deprecated - don't export, use passengersValues instead
export const passengersSelector = state => _.get(state, "form.reservation.passengers") || []

const passengerSelector = createSelector(
  passengersSelector,
  passengers => index => passengers[index],
)

export const getFormattedPassengers = createSelector(
  passengersSelector,
  passengers => passengers.map(paxObjectMapper),
)

// Return passengers with their categories based on passengers birth dates.
// WARNING: Do not use until reservation form is fully loaded.
export const passengersCategoryDecoratedSelector = createSelector(
  passengersSelector,
  getJourney,
  getThresholds,
  (passengers, journey, thresholds) =>
    passengers.map(passenger => {
      const determinedCategory = Passenger.determineTypeFromInput(
        passenger.birthDay.value,
        passenger.birthMonth.value,
        passenger.birthYear.value,
        journey.getDeparture().getIn(["when", "local"]),
        thresholds,
        true,
      )
      return { ...passenger, determinedCategory }
    }),
)

export const trolleyAllowedForInfantSelector = createSelector(
  getInfantsConditions,
  infantsConditions => infantsConditions && infantsConditions.get("trolley"), // undefined means we dont
)

export const passengersValuesSelector = createSelector(
  passengersCategoryDecoratedSelector,
  getCombinations,
  getBaggageInitialCombinations,
  getJourney,
  (passengersCategoryDecorated, combinations, baggageInitialCombinations, journey) =>
    passengersCategoryDecorated.map(passenger => {
      const passengerValues = getValues(passenger)
      const category = passengerValues.category || "adult"
      // Strip middlename, when not required
      // Scenario: middlename filled, changed to diff nationality without middlename
      // Still valid in store while not visible in form => omit it
      if (!shouldShowMiddlename(journey, passengerValues.nationality)) {
        passengerValues.middlename = null
      }

      const holdBagCombination = fp.get(
        ["holdBag", passengerValues.baggage.holdBag, "indices"],
        combinations,
      )
      const handBagCombination = fp.get(
        ["handBag", passengerValues.baggage.holdBag, "indices"],
        combinations,
      )
      const handBag = _.isUndefined(handBagCombination)
        ? baggageInitialCombinations[category].handBag
        : passengerValues.baggage.handBag
      const holdBag = _.isUndefined(holdBagCombination)
        ? baggageInitialCombinations[category].holdBag
        : passengerValues.baggage.holdBag

      return {
        category,
        determinedCategory: passenger.determinedCategory,
        // Default, rewritten by passengersValues
        firstname: "",
        lastname: "",
        ...passengerValues,
        baggage: {
          handBag,
          holdBag,
        },
      }
    }),
)

export const passengerValuesSelector = createSelector(
  passengersValuesSelector,
  passengersValues => index => passengersValues[index],
)

export const passengersHoldCombinationsSelector = createSelector(
  passengersValuesSelector,
  getCombinations,
  (passengersValues, combinations) =>
    passengersValues.map(({ baggage: { holdBag } }) => combinations.holdBag[holdBag].indices),
)

export const passengersHandCombinationsSelector = createSelector(
  passengersValuesSelector,
  getCombinations,
  (passengersValues, combinations) =>
    passengersValues.map(({ baggage: { handBag } }) => combinations.handBag[handBag].indices),
)

// Returns overall passengers count as number
// WARNING: Do not use until reservation form is fully loaded
export const passengersCountSelector = createSelector(
  passengersSelector,
  passengers => passengers.length,
)

export const passengersCountWithoutInfantsSelector = createSelector(
  passengersCategoryDecoratedSelector,
  passengers => passengers.filter(passenger => passenger.determinedCategory !== "infant").length,
)

// Returns PassengersCount object with count for individual passenger category
// WARNING: Do not use until reservation form is fully loaded
export const passengersCategoryCountSelector = createSelector(
  passengersValuesSelector,
  passengersValues => {
    const passengerCategoriesCount = _.countBy(passengersValues, p =>
      p.category === PASSENGER_CATEGORIES.infant ? "infants" : "adults",
    )

    return new PassengersCount(passengerCategoriesCount)
  },
)

export const isOnePassengerOneFlightSelector = createSelector(
  getJourney,
  passengersCountSelector,
  (journey, passengersCount) => {
    const numberOfFlights = journey.getFlightsCount()

    return passengersCount === 1 && numberOfFlights === 1
  },
)

export const nextPassengerInitialDataSelector = createSelector(
  passengersValuesSelector,
  getBaggageInitialCombinations,
  getCombinations,
  (passengersValues, baggageInitialCombinations) => {
    if (!passengersValues.length) return {}

    return {
      nationality: _.last(passengersValues).nationality, // Use last pax nationality
      baggage: {
        handBag: baggageInitialCombinations.adult.handBag,
        holdBag: baggageInitialCombinations.adult.holdBag,
      },
    }
  },
)

export const nextInfantInitialDataSelector = createSelector(
  nextPassengerInitialDataSelector,
  passengersValuesSelector,
  (nextPassengerInitialData, passengersValues) => {
    if (!passengersValues.length) return {}
    const infantDateBirth = setDate(subYears(new Date(), 1), 1)

    return {
      ...nextPassengerInitialData,
      birthDay: getDate(infantDateBirth),
      birthMonth: getMonth(infantDateBirth + 1), // +1 due to month indexing
      birthYear: getYear(infantDateBirth),
    }
  },
)

export const shouldPassengerRequireBirthCertificateSelector = createSelector(
  getJourney,
  passengerSelector,
  passengerValuesSelector,
  (journey, passenger, passengerValues) => index => {
    const _passenger = passenger(index)

    // Check whether birth data valid
    if (
      _.some([
        _passenger.birthDay.invalid,
        _passenger.birthMonth.invalid,
        _passenger.birthYear.invalid,
      ])
    )
      return false

    const _passengerValues = passengerValues(index)

    const yearOld = differenceInYears(
      new Date(),
      new Date(_passengerValues.birthYear, _passengerValues.birthMonth, _passengerValues.birthDay),
    )

    return shouldRequireBirthCertificate(
      journey.getAirlines(),
      yearOld,
      _passengerValues.nationality,
    )
  },
)

export const getPassengerPassportLabelSelector = createSelector(
  passengerValuesSelector,
  getJourney,
  getLanguageInfo,
  (passengerValues, journey, langInfo) => index => {
    const passengerNationality = passengerValues(index).nationality

    if (INTERNATIONAL_PASSPORT_NATIONALITIES.includes(passengerNationality)) {
      const journeyCountries = journey.getCountries()
      return journeyCountries.every(country => country === passengerNationality) ||
        !INTERNATIONAL_PASSPORT_NATIONALITIES.includes(langInfo.defaultCountry)
        ? __("booking.passenger.passport.label")
        : __("booking.passenger.international.label")
    }
    return __("booking.passenger.id_number.label")
  },
)

export const getInfantsPassengersIndexesSelector = createSelector(
  passengersValuesSelector,
  passengersValues =>
    passengersValues.reduce(
      (acc, pax, index) => (pax.category === PASSENGER_CATEGORIES.infant ? acc.push(index) : acc),
      List(),
    ),
)

export const anyInsuredPassengerSelector = createSelector(
  passengersValuesSelector,
  fp.some(passenger => passenger.insurance !== "none"),
)

export const passengersAllowedInsuranceSelector = createSelector(
  passengersValuesSelector,
  isInsuranceEnabledInReservationSelector,
  (passengers, isInsuranceEnabledInReservation) =>
    passengers.filter(
      pap => isInsuranceEnabledInReservation && isInsuranceAllowedForNationality(pap.nationality),
    ),
)

export const passengersAllowedInsuranceIndicesSelector = createSelector(
  passengersSelector,
  isInsuranceEnabledInReservationSelector,
  (passengers, isInsuranceEnabledInReservation) => {
    if (!isInsuranceEnabledInReservation) return []

    return passengers
      .map((pap, index) => isInsuranceAllowedForNationality(pap.nationality.value) && index)
      .filter(fp.isNumber) // 0 is valid index, undefined not
  },
)

export const getUnfilledAutofillPassengers = createSelector(
  storedPassengersSelector,
  passengersValuesSelector,
  (storedPassengers, filledPassengers) =>
    storedPassengers.filter(
      stored => !filledPassengers.find(filled => Passenger.equals(stored, filled)),
    ),
)

export const passengerComponentSettingsSelector = createSelector(
  getAffilId,
  passengersValuesSelector,
  passengersSelector,
  getJourney,
  getLanguageInfo,
  containsFlightSelector,
  getDocumentNeed,
  shouldPassengerRequireBirthCertificateSelector,
  isInsuranceEnabledInReservationSelector,
  trolleyAllowedForInfantSelector,
  getUnfilledAutofillPassengers,
  (
    affilId,
    passengersValues,
    passengersValuesRaw,
    journey,
    langInfo,
    containsFlight,
    documentNeed,
    shouldPassengerRequireBirthCertificate,
    isInsuranceEnabledInReservation,
    trolleyAllowedForInfant,
    unfilledPassengers,
  ) => index => {
    const passenger = passengersValues[index]
    const passengerRaw = passengersValuesRaw[index]

    const nationality = passengerRaw.nationality.value || passengerRaw.nationality.initialValue
    const nationalityBasedLanguage = fp.findKey(["defaultCountry", nationality])(languages)
    const nationalityBasedLanguageInfo = languages[nationalityBasedLanguage] || langInfo
    const _shouldShowMiddlename = shouldShowMiddlename(journey, nationality)

    return {
      bagsRecheckAlways: isSetAffilId(AFFILID_BAGS_RECHECK_ALWAYS, affilId),
      category: passenger.category,
      nationality, // safe nationality

      // Placeholders
      firstNamePlaceholder: formatFirstNamePlaceholder(
        _shouldShowMiddlename,
        nationality,
        nationalityBasedLanguageInfo.firstNamePlaceholder,
      ),
      middleNamePlaceholder: formatMiddleNamePlaceholder(
        _shouldShowMiddlename,
        nationalityBasedLanguageInfo.middleNamePlaceholder,
        translate("booking.passenger.middlename_placeholder"),
      ),
      lastNamePlaceholder: formatLastnNamePlaceholder(
        nationalityBasedLanguageInfo.lastNamePlaceholder,
      ),

      // Conditions
      shouldRequirePassportExpirationDate: shouldRequirePassportExpirationDate(
        journey,
        langInfo.defaultCountry,
        nationality,
      ),
      shouldShowInfantBanner: containsFlight && passenger.category === PASSENGER_CATEGORIES.infant,
      shouldShowMiddlename: _shouldShowMiddlename,
      shouldRequireDocument: documentNeed === "booking",
      shouldRequireBirthCertificate: shouldPassengerRequireBirthCertificate(index), // related to category
      shouldOfferAutofill: Boolean(unfilledPassengers.length),
      insuranceEnabled:
        isInsuranceEnabledInReservation &&
        // defaultCountry for ab test NEW_PASSENGER_FIELDS (nationality is not pre-filled, but we know probable nationality)
        isInsuranceAllowedForNationality(nationality || langInfo.defaultCountry),
      trolleyAllowed: trolleyAllowedForInfant,
    }
  },
)

// Returns object with count for individual passenger category immediately
export const getPassengersCountsImmediately = createSelector(
  reservationFormSelector,
  passengersCategoryCountSelector,
  getOriginalPassengers,
  (isReservationFormReady, passengersCategoryCount, originalPassengers) => {
    // if reservation form is loaded
    if (isReservationFormReady) {
      // grab passengers from it
      return {
        adult: passengersCategoryCount.get("adults"),
        infant: passengersCategoryCount.get("infants"),
      }
    }

    // otherwise use URL params
    return originalPassengers
  },
)

export default createSelector(
  [
    passengerComponentSettingsSelector,
    passengersValuesSelector,
    passengerValuesSelector,
    passengersCountSelector,
    passengersCategoryCountSelector,
    trolleyAllowedForInfantSelector,
    nextPassengerInitialDataSelector,
    nextInfantInitialDataSelector,
    getInfantsPassengersIndexesSelector,
  ],
  (
    passengerComponentSettings,
    passengersValues,
    passengerValues,
    passengersCount,
    passengersCategoryCount,
    trolleyAllowedForInfant,
    nextPassengerInitialData,
    nextInfantInitialData,
    getInfantsPassengersIndexes,
  ) => ({
    passengerComponentSettings,
    passengersValues,
    passengerValues,
    passengersCount,
    passengersCategoryCount,
    trolleyAllowedForInfant,
    nextPassengerInitialData,
    nextInfantInitialData,
    getInfantsPassengersIndexes,
  }),
)
